'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

/**
 * V2.11 (2026-05-23) — left rail on /e/<slug>. Shows the visitor's
 * cross-twin conversation history. Clicking a row navigates to that
 * twin's chat page; the active twin (current slug) is highlighted.
 *
 * Loading + empty states are both quiet — this is a secondary surface
 * next to the actual chat panel.
 */
type ConvRow = {
  id: string;
  economist_id: string;
  economist_slug: string;
  economist_display_name: string;
  started_at: string;
  message_count: number;
  preview: string | null;
};

export function TwinChatRail({ activeSlug }: { activeSlug: string }) {
  const [rows, setRows] = useState<ConvRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/me/conversations', { credentials: 'include' });
        if (!r.ok) {
          if (!cancelled) setRows([]);
          return;
        }
        const body = (await r.json()) as { conversations: ConvRow[] };
        if (!cancelled) setRows(body.conversations ?? []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Group consecutive rows with the same economist_slug so the same twin
  // doesn't repeat as 5 separate cards — instead one twin entry with the
  // most-recent preview + a count of total threads.
  const grouped = (() => {
    if (!rows) return null;
    const m = new Map<string, { row: ConvRow; threads: number }>();
    for (const r of rows) {
      const cur = m.get(r.economist_slug);
      if (cur) cur.threads += 1;
      else m.set(r.economist_slug, { row: r, threads: 1 });
    }
    return Array.from(m.values()).sort((a, b) => (a.row.started_at < b.row.started_at ? 1 : -1));
  })();

  return (
    <aside className="hidden md:flex md:flex-col w-72 lg:w-80 shrink-0 border-r border-soft bg-cream/30 h-full overflow-y-auto">
      <div className="p-4 border-b border-soft">
        <p className="text-[11px] uppercase tracking-wider text-text-mute font-medium">对话历史</p>
      </div>
      <nav className="p-2">
        {grouped === null && <p className="px-3 py-2 text-xs text-text-mute italic">加载中…</p>}
        {grouped !== null && grouped.length === 0 && (
          <p className="px-3 py-2 text-xs text-text-mute italic">
            还没和其他分身聊过。先在这里聊起来。
          </p>
        )}
        {grouped?.map(({ row, threads }) => {
          const isActive = row.economist_slug === activeSlug;
          return (
            <Link
              key={row.economist_slug}
              href={`/e/${encodeURIComponent(row.economist_slug)}`}
              className={`block px-3 py-2.5 rounded-md mb-0.5 transition ${
                isActive
                  ? 'bg-warmth-dark/10 border border-warmth-dark/20'
                  : 'hover:bg-soft/40 border border-transparent'
              }`}
            >
              <div className="flex items-baseline justify-between gap-2 mb-0.5">
                <span
                  className={`text-sm font-medium truncate ${
                    isActive ? 'text-warmth-dark' : 'text-navy'
                  }`}
                >
                  {row.economist_display_name}
                </span>
                {threads > 1 && (
                  <span className="text-[10px] text-text-mute shrink-0">{threads} 次</span>
                )}
              </div>
              {row.preview && (
                <p className="text-xs text-dusk line-clamp-2 leading-snug">{row.preview}</p>
              )}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
